<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Sync Status Bar -->
    <SyncStatusBar
      v-if="syncStatus"
      :sync-status="syncStatus"
      :loading="syncing"
      @refresh="loadDashboard" />

    <!-- Header Section -->
    <div class="bg-white border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">
              Wellness Dashboard
            </h1>
            <p class="mt-1 text-sm text-gray-600">
              Track mindfulness, audio exposure, and symptoms
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <!-- Time Period Selector -->
            <TimeSelector
              :value="period"
              :options="periodOptions"
              @change="setPeriod" />

            <!-- Compare Toggle -->
            <label class="inline-flex items-center text-sm">
              <input
                type="checkbox"
                v-model="compareEnabled"
                @change="loadDashboard"
                class="rounded border-gray-300 text-gray-900 focus:ring-gray-500">
              <span class="ml-2 text-gray-600">Compare</span>
            </label>

            <!-- Refresh Button -->
            <button
              @click="loadDashboard"
              :disabled="loading"
              class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <svg
                class="w-4 h-4 mr-1.5"
                :class="{ 'animate-spin': loading }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <LoadingSkeleton v-if="loading && !dashboard" />

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div class="flex items-start">
          <svg class="w-6 h-6 text-red-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="text-sm font-medium text-red-800">
              Error loading dashboard
            </h3>
            <p class="mt-1 text-sm text-red-700">
              {{ error }}
            </p>
            <button
              @click="loadDashboard"
              class="mt-3 text-sm font-medium text-red-600 hover:text-red-500">
              Try again
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <EmptyState v-else-if="!hasData" />

      <!-- Dashboard Content -->
      <div v-else class="space-y-8">
        <!-- Stats Cards Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Mindfulness Card -->
          <MindfulnessCard
            v-if="mindfulness"
            :data="mindfulness"
            :streak="getStreak('mindfulness')"
            :comparison="comparison?.mindfulness"
            @share="shareCategory('mindfulness')" />

          <!-- Audio Exposure Card -->
          <AudioCard
            v-if="audio"
            :data="audio"
            :streak="getStreak('audio_safe')"
            :comparison="comparison?.audio"
            @share="shareCategory('audio')" />

          <!-- Symptoms Card -->
          <SymptomsCard
            v-if="symptoms"
            :data="symptoms"
            :streak="getStreak('symptom_free')"
            @share="shareCategory('symptoms')" />
        </div>

        <!-- Insights Section -->
        <InsightsCard
          v-if="insights && insights.length > 0"
          :insights="insights" />

        <!-- Goals Section -->
        <div v-if="goals && goals.length > 0" class="bg-white rounded-lg border-2 border-gray-900 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold uppercase tracking-wider">
              Goals
            </h2>
            <button
              @click="showGoalsModal = true"
              class="text-sm text-gray-600 hover:text-gray-900">
              Edit Goals
            </button>
          </div>
          <div class="space-y-3">
            <div v-for="goal in goals" :key="goal.id" class="flex items-center justify-between">
              <span class="text-sm text-gray-700">{{ formatGoalType(goal.goal_type) }}</span>
              <span class="text-sm font-bold">{{ goal.target_value }}</span>
            </div>
          </div>
        </div>

        <!-- Streaks Section -->
        <div v-if="streaks && streaks.length > 0" class="bg-white rounded-lg border-2 border-gray-900 p-6">
          <h2 class="text-lg font-bold uppercase tracking-wider mb-4">
            Streaks
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div v-for="streak in streaks" :key="streak.id" class="text-center p-4 bg-gray-50 rounded-lg">
              <p class="text-3xl font-bold text-gray-900">
                {{ streak.current_streak }}
              </p>
              <p class="text-sm text-gray-500">
                {{ formatStreakType(streak.streak_type) }}
              </p>
              <p class="text-xs text-gray-400 mt-1">
                Best: {{ streak.longest_streak }} days
              </p>
            </div>
          </div>
        </div>

        <!-- Medical Disclaimer -->
        <div class="text-center text-xs text-gray-400 py-4">
          For informational purposes only. Not intended as medical advice.
        </div>
      </div>
    </div>

    <!-- Goals Modal -->
    <GoalsModal
      v-if="showGoalsModal"
      :goals="goals"
      @close="showGoalsModal = false"
      @save="handleGoalsSaved" />
  </div>
</template>

<script>
import SyncStatusBar from '~/javascript/components/wellness/sync_status_bar.vue'
import TimeSelector from '~/javascript/components/wellness/time_selector.vue'
import LoadingSkeleton from '~/javascript/components/wellness/loading_skeleton.vue'
import EmptyState from '~/javascript/components/wellness/empty_state.vue'
import MindfulnessCard from '~/javascript/components/wellness/mindfulness_card.vue'
import AudioCard from '~/javascript/components/wellness/audio_card.vue'
import SymptomsCard from '~/javascript/components/wellness/symptoms_card.vue'
import InsightsCard from '~/javascript/components/wellness/insights_card.vue'
import GoalsModal from '~/javascript/components/wellness/goals_modal.vue'
import consumer from '~/channels/consumer'

export default {
  name: 'WellnessDashboard',

  components: {
    SyncStatusBar,
    TimeSelector,
    LoadingSkeleton,
    EmptyState,
    MindfulnessCard,
    AudioCard,
    SymptomsCard,
    InsightsCard,
    GoalsModal
  },

  props: {
    userId: {
      type: String,
      required: true
    },
    defaultPeriod: {
      type: String,
      default: '7d'
    }
  },

  data() {
    return {
      loading: true,
      syncing: false,
      error: null,
      dashboard: null,
      period: this.defaultPeriod,
      compareEnabled: false,
      showGoalsModal: false,
      subscription: null,
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: '7d', label: '7D' },
        { value: '30d', label: '30D' },
        { value: '90d', label: '90D' }
      ]
    }
  },

  computed: {
    mindfulness() {
      return this.dashboard?.mindfulness
    },
    audio() {
      return this.dashboard?.audio
    },
    symptoms() {
      return this.dashboard?.symptoms
    },
    goals() {
      return this.dashboard?.goals || []
    },
    streaks() {
      return this.dashboard?.streaks || []
    },
    insights() {
      return this.dashboard?.insights || []
    },
    syncStatus() {
      return this.dashboard?.sync_status
    },
    comparison() {
      return this.dashboard?.comparison
    },
    hasData() {
      return this.mindfulness || this.audio || this.symptoms
    }
  },

  mounted() {
    this.loadDashboard()
    this.subscribeToUpdates()
  },

  beforeUnmount() {
    this.unsubscribeFromUpdates()
  },

  methods: {
    async loadDashboard() {
      this.loading = true
      this.error = null

      try {
        const params = new URLSearchParams({
          period: this.period,
          compare_to_previous: this.compareEnabled ? 'true' : 'false'
        })

        const response = await fetch(`/api/v1/wellness/dashboard?${params}`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.dashboard = await response.json()
      } catch (err) {
        console.error('Failed to load wellness dashboard:', err)
        this.error = err.message || 'Failed to load dashboard'
      } finally {
        this.loading = false
      }
    },

    subscribeToUpdates() {
      this.subscription = consumer.subscriptions.create(
        { channel: 'WellnessChannel', user_id: this.userId },
        {
          connected: () => {
            console.log('Connected to WellnessChannel')
          },
          disconnected: () => {
            console.log('Disconnected from WellnessChannel')
          },
          received: (data) => {
            this.handleRealtimeUpdate(data)
          }
        }
      )
    },

    unsubscribeFromUpdates() {
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
    },

    handleRealtimeUpdate(data) {
      console.log('Received wellness update:', data.event)

      switch (data.event) {
        case 'data_synced':
          this.syncing = true
          // Reload after a brief delay to allow server to process
          setTimeout(() => {
            this.loadDashboard().finally(() => {
              this.syncing = false
            })
          }, 500)
          break

        case 'streak_updated':
          // Update streak in place
          if (this.dashboard?.streaks) {
            const streakIndex = this.dashboard.streaks.findIndex((s) => s.streak_type === data.streak_type)
            if (streakIndex >= 0) {
              this.dashboard.streaks[streakIndex] = {
                ...this.dashboard.streaks[streakIndex],
                current_streak: data.current_streak,
                longest_streak: data.longest_streak
              }
            }
          }
          break

        case 'insight_ready':
        case 'weekly_summary_ready':
          this.loadDashboard()
          break
      }
    },

    setPeriod(newPeriod) {
      this.period = newPeriod
      this.loadDashboard()
    },

    getStreak(streakType) {
      return this.streaks.find((s) => s.streak_type === streakType)
    },

    formatGoalType(goalType) {
      const labels = {
        mindfulness_minutes: 'Daily Mindfulness (min)',
        audio_limit: 'Audio Limit (dB)'
      }
      return labels[goalType] || goalType.replace(/_/g, ' ')
    },

    formatStreakType(streakType) {
      const labels = {
        mindfulness: 'Mindfulness',
        audio_safe: 'Safe Audio',
        symptom_free: 'Symptom-Free'
      }
      return labels[streakType] || streakType.replace(/_/g, ' ')
    },

    async shareCategory(category) {
      try {
        const response = await fetch(`/api/v1/wellness/share/summary?period=${this.period}`)
        const data = await response.json()

        if (data.image_base64) {
          // Convert base64 to blob and share
          const blob = this.base64ToBlob(data.image_base64, 'image/png')
          const file = new File([blob], 'wellness-summary.png', { type: 'image/png' })

          if (navigator.share && navigator.canShare({ files: [file]})) {
            await navigator.share({
              files: [file],
              title: 'My Wellness Summary',
              text: `Check out my ${category} wellness stats!`
            })
          } else {
            // Fallback: download the image
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'wellness-summary.png'
            a.click()
            URL.revokeObjectURL(url)
          }
        }
      } catch (err) {
        console.error('Share failed:', err)
      }
    },

    base64ToBlob(base64, contentType) {
      const byteCharacters = atob(base64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      return new Blob([byteArray], { type: contentType })
    },

    handleGoalsSaved() {
      this.showGoalsModal = false
      this.loadDashboard()
    }
  }
}
</script>
